/**
 * 广告命名模板：单个字段（位）的编辑器组件集合。
 *
 * 本文件按 PR-AT-20260626-004 重构：
 *   - 去掉 "枚举集名" 输入（Item 1）
 *   - 规则：组合 1 = INPUT_BOX 时不允许有组合 2；组合 2（若存在）必须为 SINGLE_ENUM
 *     （Item 3）；故 "添加第 2 组" 按钮在 INPUT_BOX 模式下隐藏，且不提供"添加输入框"分支
 *   - 拆出 `FieldEditorForm` 供"编辑/新增"弹窗复用（Item 5+6）
 *   - `allowSecondCombo=false`（华为）时整段隐藏组合 2 相关 UI（华为每个字段只有 1 组枚举）
 *
 * 注意：本组件不再内嵌"上移/下移/删除"按钮——这些是列表层级的操作，
 * 由 `AdNameTemplatePage` 渲染在摘要卡上。
 */

import type {
  Combo,
  EnumItem,
  SingleEnumCombo,
  TemplateField,
} from "../../types/adNameTemplate.types";
import {
  newCombo,
  newInputBoxCombo,
  newSingleEnumCombo,
  reorderItems,
} from "../../utils/adNameTemplateFactory";

const SEPARATOR_OPTIONS: { label: string; value: string }[] = [
  { label: "无（紧贴下一字段）", value: "" },
  { label: "*（星号）", value: "*" },
  { label: "%（百分号）", value: "%" },
  { label: "_（下划线）", value: "_" },
  { label: " |  （竖线）", value: "|" },
];

const COMBO_TYPE_OPTIONS: { label: string; value: Combo["combo_type"] }[] = [
  { label: "枚举", value: "SINGLE_ENUM" },
  { label: "输入框", value: "INPUT_BOX" },
];

const HINT_TEXT_MAX_LEN = 50;

// ---------- 列表用的纯表单：用于弹窗与后续可能的"内联"扩展 ----------
export interface FieldEditorFormProps {
  field: TemplateField;
  onChange: (next: TemplateField) => void;
  /**
   * 是否允许配置组合 2（第 2 组枚举）。缺省 true（腾讯保留「组合 2 可选」）；
   * 华为每个字段只有 1 组枚举，传 false 隐藏「添加第 2 组枚举」按钮及组合 2 编辑区。
   */
  allowSecondCombo?: boolean;
}

export function FieldEditorForm({ field, onChange, allowSecondCombo = true }: FieldEditorFormProps) {
  function patch(p: Partial<TemplateField>) {
    onChange({ ...field, ...p });
  }

  function setCombo(idx: 0 | 1, combo: Combo | null) {
    const combos = [...field.combos];
    if (combo === null) {
      combos.splice(idx, 1);
    } else {
      combos[idx] = combo;
    }
    patch({ combos });
  }

  function ensureCombo(idx: 0 | 1, type: Combo["combo_type"]): Combo {
    const c = field.combos[idx];
    if (c && c.combo_type === type) return c;
    const fresh: Combo =
      type === "SINGLE_ENUM"
        ? {
            combo_type: "SINGLE_ENUM",
            single_enum_combo: newSingleEnumCombo([]),
            multi_enum_combo: null,
            date_combo: null,
            input_box_combo: null,
          }
        : newInputBoxCombo();
    const combos = [...field.combos];
    combos[idx] = fresh;
    patch({ combos });
    return fresh;
  }

  function changeComboType(idx: 0 | 1, type: Combo["combo_type"]) {
    const current = field.combos[idx];
    if (current && current.combo_type === type) return;
    // 切到 INPUT_BOX 时清掉同位置的枚举；切到 SINGLE_ENUM 时清掉同位置的输入框。
    setCombo(
      idx,
      type === "SINGLE_ENUM" ? newCombo() : newInputBoxCombo(),
    );
  }

  function updateEnumItems(idx: 0 | 1, items: EnumItem[]) {
    const combo = ensureCombo(idx, "SINGLE_ENUM");
    const sec = combo.single_enum_combo as SingleEnumCombo;
    const updated: Combo = {
      combo_type: "SINGLE_ENUM",
      single_enum_combo: { ...sec, enum_items: reorderItems(items) },
      multi_enum_combo: null,
      date_combo: null,
      input_box_combo: null,
    };
    setCombo(idx, updated);
  }

  function updateInputBoxHint(idx: 0 | 1, hint: string) {
    const combo = ensureCombo(idx, "INPUT_BOX");
    const updated: Combo = {
      ...combo,
      input_box_combo: { hint_text: hint || null },
    };
    setCombo(idx, updated);
  }

  const group1Type = field.combos[0]?.combo_type ?? "SINGLE_ENUM";
  const canHaveGroup2 = group1Type === "SINGLE_ENUM"; // 规则：INPUT_BOX 时不允许有组合 2
  // 平台级开关：华为每个字段只有 1 组枚举，整段不展示组合 2 相关 UI
  const showGroup2Area = allowSecondCombo && canHaveGroup2;

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-[11.5px] font-semibold uppercase tracking-[0.06em] text-text-strong">
          字段名
        </label>
        <input
          type="text"
          className="leo-input"
          placeholder="如：拉新 / 代理 / 渠道号 / 客户经理"
          value={field.name}
          onChange={(e) => patch({ name: e.target.value })}
          autoFocus
        />
      </div>

      <div className="space-y-4">
        {/* Group 1 */}
        <ComboGroupEditor
          groupLabel="组合 1"
          combo={field.combos[0] ?? null}
          onChangeType={(t) => changeComboType(0, t)}
          onChange={(c) => setCombo(0, c)}
          onUpdateItems={(items) => updateEnumItems(0, items)}
          onUpdateHint={(hint) => updateInputBoxHint(0, hint)}
        />

        {/* 枚举组间分隔符：仅在已存在第 2 组时显示（作用于枚举组 1 与枚举组 2 之间） */}
        {allowSecondCombo && field.combos.length >= 2 ? (
          <div className="flex items-center gap-3 rounded-md border border-line-subtle bg-da-inset px-3 py-2">
            <span className="text-[11px] uppercase tracking-[0.06em] text-text-muted">
              与下一字段分隔
            </span>
            <select
              className="leo-select w-auto min-w-[180px]"
              value={field.separator_to_next ?? ""}
              onChange={(e) =>
                patch({
                  separator_to_next: e.target.value === "" ? null : e.target.value,
                })
              }
            >
              {SEPARATOR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className="ml-auto text-[11px] text-text-muted-weak">
              （作用于枚举组 1 与枚举组 2 之间）
            </span>
          </div>
        ) : null}

        {/* Group 2 (optional)：仅当 组合 1 = SINGLE_ENUM 时可加 */}
        {showGroup2Area && field.combos.length >= 2 ? (
          <ComboGroupEditor
            groupLabel="组合 2"
            combo={field.combos[1] ?? null}
            onChangeType={(t) => changeComboType(1, t)}
            onChange={(c) => setCombo(1, c)}
            onUpdateItems={(items) => updateEnumItems(1, items)}
            onUpdateHint={(hint) => updateInputBoxHint(1, hint)}
            onRemove={() => setCombo(1, null)}
            allowRemove
          />
        ) : showGroup2Area ? (
          <button
            type="button"
            onClick={() => setCombo(1, newCombo())}
            className="leo-btn-ghost w-full text-[12px]"
          >
            + 添加第 2 组枚举（带 * / % 分隔）
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ---------- 单组编辑器（被 FieldEditorForm 复用） ----------
interface ComboGroupProps {
  groupLabel: string;
  combo: Combo | null;
  onChangeType: (type: Combo["combo_type"]) => void;
  onChange: (next: Combo) => void;
  onUpdateItems: (items: EnumItem[]) => void;
  onUpdateHint: (hint: string) => void;
  onRemove?: () => void;
  allowRemove?: boolean;
}

function ComboGroupEditor({
  groupLabel,
  combo,
  onChangeType,
  onChange,
  onUpdateItems,
  onUpdateHint,
  onRemove,
  allowRemove,
}: ComboGroupProps) {
  const type = combo?.combo_type ?? "SINGLE_ENUM";
  const items = combo?.single_enum_combo?.enum_items ?? [];
  const hint = combo?.input_box_combo?.hint_text ?? "";

  function addItem() {
    const next: EnumItem[] = [
      ...items,
      {
        id: `ei_${Date.now().toString(36)}_${items.length + 1}`,
        item_order: items.length,
        item_code: "",
        item_meaning: "",
      },
    ];
    onUpdateItems(next);
  }

  function updateItem(idx: number, patchObj: Partial<EnumItem>) {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patchObj } : it));
    onUpdateItems(next);
  }

  function removeItem(idx: number) {
    onUpdateItems(items.filter((_, i) => i !== idx));
  }

  function patchCombo(patchObj: Partial<Combo>) {
    if (!combo) return;
    onChange({ ...combo, ...patchObj });
  }

  return (
    <div className="rounded-lg border border-line-subtle bg-da-inset p-3.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-text-strong">
            {groupLabel}
          </span>
          <select
            className="leo-select w-auto min-w-[100px] py-1 text-[11.5px]"
            value={type}
            onChange={(e) => onChangeType(e.target.value as Combo["combo_type"])}
            aria-label={`${groupLabel}类型`}
          >
            {COMBO_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {allowRemove && onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="text-[11px] text-text-muted-weak hover:text-semantic-danger"
          >
            移除该组
          </button>
        ) : null}
      </div>

      {type === "INPUT_BOX" ? (
        <div>
          <div className="mb-1.5 text-[10.5px] uppercase tracking-[0.06em] text-text-muted-weak">
            输入框提示文本（≤{HINT_TEXT_MAX_LEN} 字符，可留空）
          </div>
          <input
            type="text"
            className="leo-input"
            placeholder="如：客户经理 / 投放日期 / 备注"
            value={hint ?? ""}
            maxLength={HINT_TEXT_MAX_LEN}
            onChange={(e) => onUpdateHint(e.target.value)}
          />
          <div className="mt-1.5 text-[10.5px] text-text-muted-weak">
            实际拼接广告名时此处会替换为人工填入的字符串。
          </div>
        </div>
      ) : (
        <>
          <div className="mb-1.5 grid grid-cols-[24px_minmax(0,1fr)_minmax(0,2fr)_36px] gap-2 text-[10.5px] uppercase tracking-[0.06em] text-text-muted-weak">
            <span>#</span>
            <span>Code（拼接值）</span>
            <span>含义</span>
            <span></span>
          </div>

          <div className="space-y-1.5">
            {items.length === 0 ? (
              <div className="rounded-md border border-dashed border-line-default px-3 py-2.5 text-center text-[11.5px] text-text-muted-weak">
                暂无枚举项，请点击下方「+ 添加枚举值」
              </div>
            ) : null}
            {items.map((it, idx) => (
              <div
                key={it.id}
                className="grid grid-cols-[24px_minmax(0,1fr)_minmax(0,2fr)_36px] items-center gap-2"
              >
                <span className="text-center text-[11px] text-text-muted">
                  {idx + 1}
                </span>
                <input
                  type="text"
                  className="leo-input"
                  placeholder="如 YK / 41城 / *MTCL"
                  value={it.item_code}
                  onChange={(e) => updateItem(idx, { item_code: e.target.value })}
                />
                <input
                  type="text"
                  className="leo-input"
                  placeholder="如 亿科"
                  value={it.item_meaning}
                  onChange={(e) => updateItem(idx, { item_meaning: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="text-text-muted-weak hover:text-semantic-danger"
                  title="删除"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addItem}
            className="leo-btn-ghost mt-2.5 w-full text-[11.5px]"
          >
            + 添加枚举值
          </button>
        </>
      )}
    </div>
  );
}
